/** Conversations (docs/API.md "Conversations", docs/CONVERSATIONS.md): the owner's threads, approvals and memory. */
import {
  Approval,
  ApprovalParams,
  CreateThreadBody,
  DecideApprovalBody,
  ListThreadsQuery,
  MemoryClosed,
  MemoryFact,
  MemoryFacts,
  MemoryParams,
  MyMemoryQuery,
  Message,
  MessagePage,
  PatchThreadBody,
  PostMessageBody,
  PutMemoryBody,
  ThreadDetail,
  ThreadMessagesQuery,
  ThreadPage,
  ThreadParams,
  ThreadStreamEvent,
  ThreadStreamQuery,
} from '../conversations.js'
import { defineRoute, type RouteDef } from './define.js'

export const conversationRoutes = {
  listThreads: defineRoute({
    method: 'GET',
    path: '/threads',
    auth: 'session',
    summary: 'The caller’s threads, most recently active first',
    query: ListThreadsQuery,
    response: ThreadPage,
  }),
  createThread: defineRoute({
    method: 'POST',
    path: '/threads',
    auth: 'session',
    summary: 'Open the desk, an agent thread or a support thread (the desk and an agent thread are returned if they exist)',
    body: CreateThreadBody,
    status: 201,
    response: ThreadDetail,
  }),
  getThread: defineRoute({
    method: 'GET',
    path: '/threads/:id',
    auth: 'session',
    summary: 'One of the caller’s threads with its work state and pending approvals',
    params: ThreadParams,
    response: ThreadDetail,
  }),
  patchThread: defineRoute({
    method: 'PATCH',
    path: '/threads/:id',
    auth: 'session',
    summary: 'Rename, archive or reopen a thread',
    params: ThreadParams,
    body: PatchThreadBody,
    response: ThreadDetail,
  }),
  listThreadMessages: defineRoute({
    method: 'GET',
    path: '/threads/:id/messages',
    auth: 'session',
    summary: 'Messages after a seq, oldest first',
    params: ThreadParams,
    query: ThreadMessagesQuery,
    response: MessagePage,
  }),
  postThreadMessage: defineRoute({
    method: 'POST',
    path: '/threads/:id/messages',
    auth: 'session',
    summary: 'Write the user’s message and start the agents’ turn',
    params: ThreadParams,
    body: PostMessageBody,
    status: 201,
    response: Message,
  }),
  streamThread: defineRoute({
    method: 'GET',
    path: '/threads/:id/stream',
    auth: 'session',
    summary: 'SSE of the thread: messages, approval changes and work state; resume by Last-Event-ID or ?after=',
    params: ThreadParams,
    query: ThreadStreamQuery,
    response: ThreadStreamEvent,
    stream: true,
  }),
  approveApproval: defineRoute({
    method: 'POST',
    path: '/approvals/:id/approve',
    auth: 'session',
    summary: 'Approve a pending proposal (idempotent); the runtime then executes it',
    params: ApprovalParams,
    body: DecideApprovalBody,
    response: Approval,
  }),
  rejectApproval: defineRoute({
    method: 'POST',
    path: '/approvals/:id/reject',
    auth: 'session',
    summary: 'Reject a pending proposal (idempotent)',
    params: ApprovalParams,
    body: DecideApprovalBody,
    response: Approval,
  }),
  listMyMemory: defineRoute({
    method: 'GET',
    path: '/me/memory',
    auth: 'session',
    summary: 'The caller’s memory facts (open ones, or with history)',
    query: MyMemoryQuery,
    response: MemoryFacts,
  }),
  putMyMemory: defineRoute({
    method: 'PUT',
    path: '/me/memory/:key',
    auth: 'session',
    summary: 'State a fact as the user (closes the previous one under the key)',
    params: MemoryParams,
    body: PutMemoryBody,
    response: MemoryFact,
  }),
  deleteMyMemory: defineRoute({
    method: 'DELETE',
    path: '/me/memory/:key',
    auth: 'session',
    summary: 'Close the open fact under the key (idempotent)',
    params: MemoryParams,
    response: MemoryClosed,
  }),
} as const satisfies Record<string, RouteDef>
